import { useState } from "react";
import { CatalogImage } from "../components/CatalogImage";
import { ProductCard } from "../components/ProductCard";
import type { CatalogFilterOptions, CatalogFilters } from "../lib/catalog";
import type { AppRoute } from "../lib/routes";
import type { Product, ProductCategory } from "../types/catalog";
import "./CatalogPage.css";

type CatalogPageProps = {
  activeRoute: AppRoute;
  catalogStatus: string;
  catalogFilterOptions: CatalogFilterOptions;
  catalogFilters: CatalogFilters;
  curatedProducts: Product[];
  isAdmin: boolean;
  fitment: string;
  fitmentOptions: string[];
  productSaveStatus: string;
  routeCategory: string;
  searchSelectionLabel: string;
  showSearchResults: boolean;
  visibleCategories: ProductCategory[];
  onAddToCart: (product: Product) => void;
  onCatalogFilterChange: (filters: CatalogFilters) => void;
  onFitmentChange: (fitment: string) => void;
  onNavigateToCategory: (category: string) => void;
  onNavigateToProduct: (product: Product) => void;
  onTogglePromoted: (product: Product) => void;
};

export function CatalogPage({
  activeRoute,
  catalogStatus,
  catalogFilterOptions,
  catalogFilters,
  curatedProducts,
  isAdmin,
  fitment,
  fitmentOptions,
  productSaveStatus,
  routeCategory,
  searchSelectionLabel,
  showSearchResults,
  visibleCategories,
  onAddToCart,
  onCatalogFilterChange,
  onFitmentChange,
  onNavigateToCategory,
  onNavigateToProduct,
  onTogglePromoted,
}: CatalogPageProps) {
  const [areAllCategoriesVisible, setAreAllCategoriesVisible] = useState(false);
  const hasHiddenCategories = visibleCategories.length > 9;
  const displayedCategories = areAllCategoriesVisible ? visibleCategories : visibleCategories.slice(0, 9);
  const hasActiveFilters = showSearchResults && (Boolean(fitment.trim()) || Object.values(catalogFilters).some(Boolean));
  const isCategoryPage = activeRoute === "category" && Boolean(routeCategory);
  const introTitle = isCategoryPage ? "Truefit Automotive Solutions" : "Welcome to Truefit Automotive Solutions";
  const introDescription =
    "Your one-stop shop for car parts and accessories in Kenya. Find parts that fit, place an order and get parts delivered to your location.";
  const productsHeading = showSearchResults
    ? "Results"
    : isCategoryPage
      ? `${routeCategory} for sale`
      : "Catalogue";
  const productsDescription = showSearchResults
    ? "Review the closest matches and use filters to narrow by fitment, brand, condition, or year."
    : isCategoryPage
      ? `Shop available ${routeCategory.toLowerCase()} with prices, stock status, SKU details, and vehicle compatibility.`
      : "Browse available parts with prices, stock status, SKU details, and vehicle compatibility.";

  return (
    <section className="catalog page" aria-labelledby="catalog-title">
      <div className="page-heading">
        <h1 className="sr-only" id="catalog-title">
          {activeRoute === "category" ? routeCategory : "Catalogue"}
        </h1>
        {isAdmin ? (
          <span className="role-pill">Admin</span>
        ) : null}
      </div>

      {!showSearchResults ? (
        <section className="catalog-intro" aria-labelledby="catalog-intro-title">
          {!isCategoryPage ? <p className="eyebrow">Truefit Automotive Solutions</p> : null}
          <h2 id="catalog-intro-title">{introTitle}</h2>
          {!isCategoryPage ? <p>{introDescription}</p> : null}
        </section>
      ) : null}

      {activeRoute === "catalog" && !showSearchResults ? (
        <section className="category-section" aria-label="Product categories">
          <div className="category-grid">
            {displayedCategories.map((category) => (
              <button
                className="category-tile"
                key={category.name}
                type="button"
                onClick={() => onNavigateToCategory(category.name)}
              >
                <CatalogImage
                  className="category-tile__image"
                  decorative
                  preset="categoryTile"
                  storageKey={category.imageStorageKey}
                />
                <span>{category.name}</span>
              </button>
            ))}
          </div>
          {hasHiddenCategories ? (
            <button
              className="button button--ghost category-view-toggle"
              type="button"
              onClick={() => setAreAllCategoriesVisible((currentValue) => !currentValue)}
            >
              {areAllCategoriesVisible ? "Show less" : "View all categories"}
            </button>
          ) : null}
        </section>
      ) : null}

      {showSearchResults ? (
        <section className="catalog-results" aria-label="Search results summary">
          <div className="catalog-results__content">
            <p className="eyebrow">Search results</p>
            <h2>{searchSelectionLabel}</h2>
            <p>Filter the matches below to narrow the list.</p>
          </div>
        </section>
      ) : null}

      {productSaveStatus ? <p className="form-status">{productSaveStatus}</p> : null}
      {catalogStatus ? <p className="form-status">{catalogStatus}</p> : null}

      {showSearchResults ? (
        <section className="catalog-filters catalog-filters--strip" aria-label="Search filters">
          <div className="catalog-filters__grid">
            <label>
              Fitment
              <select
                value={fitment}
                onChange={(event) => onFitmentChange(event.target.value)}
              >
                <option value="">Any fitment</option>
                {fitmentOptions.map((fitmentOption) => (
                  <option key={fitmentOption} value={fitmentOption}>
                    {fitmentOption}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Brand
              <select
                value={catalogFilters.brand}
                onChange={(event) => onCatalogFilterChange({ ...catalogFilters, brand: event.target.value })}
              >
                <option value="">Any brand</option>
                {catalogFilterOptions.brands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Make
              <select
                value={catalogFilters.make}
                onChange={(event) => onCatalogFilterChange({ ...catalogFilters, make: event.target.value })}
              >
                <option value="">Any make</option>
                {catalogFilterOptions.makes.map((make) => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Model
              <select
                value={catalogFilters.model}
                onChange={(event) => onCatalogFilterChange({ ...catalogFilters, model: event.target.value })}
              >
                <option value="">Any model</option>
                {catalogFilterOptions.models.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Year
              <select
                value={catalogFilters.year}
                onChange={(event) => onCatalogFilterChange({ ...catalogFilters, year: event.target.value })}
              >
                <option value="">Any year</option>
                {catalogFilterOptions.years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Engine
              <select
                value={catalogFilters.engine}
                onChange={(event) => onCatalogFilterChange({ ...catalogFilters, engine: event.target.value })}
              >
                <option value="">Any engine</option>
                {catalogFilterOptions.engines.map((engine) => (
                  <option key={engine} value={engine}>
                    {engine}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Condition
              <select
                value={catalogFilters.condition}
                onChange={(event) =>
                  onCatalogFilterChange({ ...catalogFilters, condition: event.target.value })
                }
              >
                <option value="">Any condition</option>
                {catalogFilterOptions.conditions.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {hasActiveFilters ? (
            <button
              className="button button--ghost catalog-filters__clear"
              type="button"
              onClick={() => {
                onFitmentChange("");
                onCatalogFilterChange({
                  brand: "",
                  condition: "",
                  engine: "",
                  make: "",
                  model: "",
                  year: "",
                });
              }}
            >
              Clear filters
            </button>
          ) : null}
        </section>
      ) : null}

      <section className="feed-section" aria-label={showSearchResults ? "Search results" : "Products"}>
        <div className="section-heading product-section-heading">
          <div className="section-heading__content">
            <h2>{productsHeading}</h2>
            <p>{productsDescription}</p>
          </div>
        </div>
        {curatedProducts.length > 0 ? (
          <div className="product-grid">
            {curatedProducts.map((product) => (
              <ProductCard
                isAdmin={isAdmin}
                key={product.id}
                product={product}
                onAddToCart={onAddToCart}
                onOpenProduct={onNavigateToProduct}
                onTogglePromoted={(nextProduct) => {
                  onTogglePromoted(nextProduct);
                }}
              />
            ))}
          </div>
        ) : catalogStatus ? null : (
          <p className="empty-state">
            {showSearchResults ? "No parts match this search." : "No parts available."}
          </p>
        )}
      </section>
    </section>
  );
}
